// =============================================================================
// UserActions.jsx
// -----------------------------------------------------------------------------
// Self-contained "···" actions dropdown for the Users table ACTIONS column.
// Handles: Verify User (opens manage modal), Suspend/Activate, Delete.
//
// Props:
//   user            — the user row object
//   onVerify        — () => void   — opens the UserManageModal for this user
//   onToggleStatus  — (user) => Promise<void>
//   onDelete        — (user) => Promise<void>
//   isOpen          — bool   — whether THIS row's dropdown is open
//   onToggle        — () => void   — toggle open/close (managed by parent)
// =============================================================================

import React, { useState, useRef, useEffect } from 'react';
import './UserActions.css';

const UserActions = ({ user, onVerify, onToggleStatus, onDelete, isOpen, onToggle }) => {
  // ── Inline confirm state ────────────────────────────────────────────────────
  // We keep confirmations inside the dropdown (no window.confirm) so the UX
  // is clean and consistent.  'pending' means the strip is visible.
  const [confirm, setConfirm] = useState(null); // null | 'suspend' | 'activate' | 'delete'
  const [busy, setBusy]       = useState(false);

  const dropdownRef = useRef(null);

  // Close dropdown + reset confirm when it loses open state
  useEffect(() => {
    if (!isOpen) setConfirm(null);
  }, [isOpen]);

  // ── Action handlers ─────────────────────────────────────────────────────────
  const handleVerify = (e) => {
    e.stopPropagation();
    onToggle();         // close dropdown
    onVerify(user);     // open UserManageModal in parent
  };

  const requestSuspend = (e) => {
    e.stopPropagation();
    setConfirm(user.is_active ? 'suspend' : 'activate');
  };

  const requestDelete = (e) => {
    e.stopPropagation();
    setConfirm('delete');
  };

  const handleConfirmYes = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      if (confirm === 'delete') {
        await onDelete(user);
      } else {
        await onToggleStatus(user);
      }
    } finally {
      setBusy(false);
      setConfirm(null);
      onToggle(); // close after action
    }
  };

  const handleConfirmNo = (e) => {
    e.stopPropagation();
    setConfirm(null);
  };

  // ── Confirm strip copy ──────────────────────────────────────────────────────
  const confirmCopy = {
    suspend:  { msg: `Suspend ${user.username}?`,                        yes: '🔒 Yes, Suspend', danger: true  },
    activate: { msg: `Activate ${user.username}?`,                       yes: '✅ Yes, Activate', danger: false },
    delete:   { msg: `Permanently delete ${user.username}? No undo.`,   yes: '🗑️ Delete',       danger: true  },
  };

  return (
    <div className="ua-cell" ref={dropdownRef}>
      {/* ··· button */}
      <button
        className="ua-menu-btn"
        title="Actions"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
      >
        ···
      </button>

      {isOpen && (
        <div className="ua-dropdown">
          {/* ── Default menu items ─────────────────────────────── */}
          {!confirm && (
            <>
              <button onClick={handleVerify}>
                🪪 Verify User
              </button>

              <button onClick={requestSuspend}>
                {user.is_active ? '🔒 Suspend' : '✅ Activate'}
              </button>

              <button className="ua-dropdown-danger" onClick={requestDelete}>
                🗑️ Delete
              </button>
            </>
          )}

          {/* ── Inline confirm strip ───────────────────────────── */}
          {confirm && confirmCopy[confirm] && (
            <div className="ua-confirm-strip">
              <p>{confirmCopy[confirm].msg}</p>
              <div className="ua-confirm-strip-actions">
                <button
                  className="ua-confirm-yes"
                  style={!confirmCopy[confirm].danger ? { background: '#16a34a' } : {}}
                  disabled={busy}
                  onClick={handleConfirmYes}
                >
                  {busy ? 'Wait…' : confirmCopy[confirm].yes}
                </button>
                <button className="ua-confirm-no" disabled={busy} onClick={handleConfirmNo}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserActions;

